// CAMINHO: src/components/documents/CaseDocuments.tsx
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { useToast } from "@/hooks/use-toast";
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Upload, FileText, Download, Trash2, Paperclip, RefreshCw, Eye } from 'lucide-react';
import { DocumentViewer } from './DocumentViewer';

// Define o tipo para um documento, espelhando a tabela `case_documents`
export type CaseDocument = {
  id: string;
  created_at: string;
  file_name: string;
  file_path: string;
  file_size: number;
  document_type?: string;
  case_id: string;
  user_id?: string;
};

interface CaseDocumentsProps {
  caseId: string;
}

export const CaseDocuments = ({ caseId }: CaseDocumentsProps) => {
  const [documents, setDocuments] = useState<CaseDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [forceReload, setForceReload] = useState(0); // Força reload
  const [selectedDocument, setSelectedDocument] = useState<CaseDocument | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [description, setDescription] = useState<string>('');
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      // Usar any para contornar o problema de tipos
      const { data, error } = await (supabase as any)
        .from('case_documents')
        .select('*')
        .eq('case_id', caseId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (err: any) {
      toast({
        title: "Erro ao buscar documentos",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [caseId, toast, forceReload]); // Adicionar forceReload como dependência

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const sanitizeFileName = (fileName: string) => {
    // Remove acentos e caracteres especiais
    const normalized = fileName.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    // Remove caracteres especiais e espaços
    const sanitized = normalized.replace(/[^a-zA-Z0-9.-]/g, '_');
    // Remove múltiplos underscores consecutivos
    return sanitized.replace(/_+/g, '_');
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Verificar tamanho do arquivo (50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast({
        title: "Arquivo muito grande",
        description: "O arquivo deve ter no máximo 50MB.",
        variant: "destructive",
      });
      return;
    }

    // Verificar tipo de arquivo - Lista mais abrangente
    const allowedTypes = [
      'image/*',
      'application/pdf',
      'application/msword', // .doc
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/vnd.ms-word', // .doc (alternativo)
      'application/vnd.ms-word.document.12', // .docx (alternativo)
      'text/plain',
      'application/octet-stream' // Fallback para arquivos sem MIME type definido
    ];
    
    // Verificar por extensão também
    const allowedExtensions = ['.pdf', '.doc', '.docx', '.txt', '.jpg', '.jpeg', '.png', '.gif'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    const isAllowedType = allowedTypes.some(type => {
      if (type.endsWith('/*')) {
        return file.type.startsWith(type.slice(0, -1));
      }
      return file.type === type;
    });

    const isAllowedExtension = allowedExtensions.includes(fileExtension);

    // Aceitar se for tipo válido OU extensão válida
    if (!isAllowedType && !isAllowedExtension) {
      console.log('Tipo de arquivo rejeitado:', {
        fileName: file.name,
        fileType: file.type,
        fileExtension: fileExtension,
        allowedTypes: allowedTypes,
        allowedExtensions: allowedExtensions
      });
      
      toast({
        title: "Tipo de arquivo não suportado",
        description: `Arquivo: ${file.name} (${file.type}). Apenas imagens, PDFs, documentos Word e arquivos de texto são permitidos.`,
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    toast({ title: "Enviando arquivo...", description: file.name });

    // Adicionar timestamp único para evitar conflitos
    const timestamp = Date.now();
    // Sanitizar o nome do arquivo para evitar problemas com acentos
    const sanitizedFileName = sanitizeFileName(file.name);
    const filePath = `${caseId}/${timestamp}-${sanitizedFileName}`;

    try {
      // Tentar fazer upload para o storage
      const { error: uploadError } = await supabase.storage
        .from('case-documents')
        .upload(filePath, file);

      if (uploadError) {
        console.error('Erro de upload:', uploadError);
        throw new Error(`Erro no upload: ${uploadError.message}`);
      }

      // Inserir registro no banco
      const user = (await supabase.auth.getUser()).data.user;
      const { error: dbError } = await (supabase as any)
        .from('case_documents')
        .insert({
          case_id: caseId,
          file_name: file.name, // Manter o nome original para exibição
          file_path: filePath,
          file_size: file.size,
          document_type: file.type || 'application/octet-stream', // Fallback se não tiver tipo
          user_id: user?.id, // Adicionar user_id explicitamente
        });

      if (dbError) {
        console.error('Erro no banco:', dbError);
        throw new Error(`Erro no banco: ${dbError.message}`);
      }

      // Registrar entrada na timeline (ajusta nomes conforme schema atual)
      try {
        await (supabase as any)
          .from('case_timeline_entries')
          .insert({
            case_id: caseId,
            type: 'document',
            title: file.name,
            description: description || null,
            entry_date: new Date().toISOString(),
            details: { file_path: filePath, file_size: file.size, user_id: user?.id || null },
          });
      } catch (e) {
        console.warn('Falha ao registrar timeline (documento):', e);
      }

      toast({ title: "Sucesso!", description: "Documento enviado com sucesso." });
      setForceReload(prev => prev + 1); // Força reload dos documentos
    } catch (err: any) {
      console.error('Erro completo:', err);
      toast({ 
        title: "Erro no Upload", 
        description: err.message || "Erro desconhecido ao fazer upload do arquivo.", 
        variant: "destructive" 
      });
    } finally {
      setUploading(false);
      if(fileInputRef.current) fileInputRef.current.value = "";
      setDescription('');
    }
  };

  const handleDelete = async (doc: CaseDocument) => {
    if (!window.confirm(`Tem certeza que deseja excluir o arquivo "${doc.file_name}"?`)) return;

    try {
      // Primeiro, remover do storage
      const { error: storageError } = await supabase.storage
        .from('case-documents')
        .remove([doc.file_path]);

      if (storageError) throw storageError;

      // Depois, remover do banco
      const { error: dbError } = await (supabase as any)
        .from('case_documents')
        .delete()
        .eq('id', doc.id);

      if (dbError) throw dbError;

      toast({ title: "Sucesso", description: "Documento excluído com sucesso." });
      setForceReload(prev => prev + 1); // Força reload dos documentos
    } catch (err: any) {
      toast({ 
        title: "Erro ao Excluir", 
        description: err.message || "Erro desconhecido ao excluir o arquivo.", 
        variant: "destructive" 
      });
    }
  };

  const handleDownload = async (doc: CaseDocument) => {
    try {
      const { data, error } = await supabase.storage
        .from('case-documents')
        .createSignedUrl(doc.file_path, 60); // Link válido por 60 segundos

      if (error) throw error;
      
      // Abre o link em uma nova aba para iniciar o download
      window.open(data.signedUrl, '_blank');

    } catch (err: any) {
      toast({ 
        title: "Erro no Download", 
        description: err.message || "Erro desconhecido ao fazer download do arquivo.", 
        variant: "destructive" 
      });
    }
  };

  const handleViewDocument = (doc: CaseDocument) => {
    setSelectedDocument(doc);
    setViewerOpen(true);
  };

  const formatBytes = (bytes: number, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  const handleForceReload = () => {
    setForceReload(prev => prev + 1);
    toast({ title: "Recarregando...", description: "Forçando atualização dos documentos." });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Documentos</h3>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleForceReload}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {/* Descrição opcional para registrar na timeline */}
      <div className="space-y-2">
        <label htmlFor="doc-description" className="text-sm font-medium">Descrição do documento (opcional)</label>
        <Textarea
          id="doc-description"
          placeholder="Adicione uma descrição para aparecer na timeline"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
        />
      </div>

      <Input type="file" ref={fileInputRef} onChange={handleUpload} disabled={uploading} className="hidden" id="file-upload" />
      <Button size="sm" className="w-full" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
        {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
        Enviar Documento
      </Button>

      {loading && <div className="flex justify-center items-center p-4"><Loader2 className="w-5 h-5 animate-spin" /></div>}

      {!loading && documents.length > 0 ? (
        <ul className="space-y-2">
          {documents.map((doc) => (
            <li key={doc.id} className="flex items-center justify-between p-3 rounded-lg border bg-muted/50 hover:bg-muted/80 transition-colors">
              <div className="flex items-center gap-3 truncate flex-1">
                <FileText className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                <div className="truncate min-w-0">
                    <p className="font-semibold text-sm truncate" title={doc.file_name}>{doc.file_name}</p>
                    <p className="text-xs text-muted-foreground">{formatBytes(doc.file_size)}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-7 w-7 text-blue-600 hover:text-blue-700" 
                  onClick={() => handleViewDocument(doc)}
                  title="Visualizar"
                >
                    <Eye className="w-4 h-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-7 w-7 text-green-600 hover:text-green-700" 
                  onClick={() => handleDownload(doc)}
                  title="Download"
                >
                    <Download className="w-4 h-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-7 w-7 text-destructive hover:text-destructive" 
                  onClick={() => handleDelete(doc)}
                  title="Excluir"
                >
                    <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        !loading && (
            <div className="text-center text-sm text-muted-foreground py-8">
                <Paperclip className="mx-auto w-8 h-8 mb-2 text-gray-400" />
                <p>Nenhum documento anexado.</p>
                <p className="text-xs mt-1">Clique em "Enviar Documento" para adicionar arquivos.</p>
            </div>
        )
      )}

      {/* Visualizador de Documentos */}
      {selectedDocument && (
        <DocumentViewer
          document={selectedDocument}
          isOpen={viewerOpen}
          onClose={() => {
            setViewerOpen(false);
            setSelectedDocument(null);
          }}
        />
      )}
    </div>
  );
};